import { Extension, RawCommands } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { InputRule } from '@tiptap/core';
import { Node as ProsemirrorNode } from 'prosemirror-model';
import { Editor } from '@tiptap/react';
import { REFERENCE_DOUBLE_MUSTACHE_PATTERN } from '../regex';

/**
 * This extension transforms reference patterns like {{inputs.ID}} and {{steps.ID}}
 * into actual Reference node components when content is set in the editor.
 *
 * The transformation happens when content is set to the editor, either initially or
 * when updated. It searches for the pattern and replaces with Reference nodes.
 */

// Create a plugin key for our transformer
export const ReferenceTransformerKey = new PluginKey('referenceTransformer');

// Define the reference type explicitly
interface ReferenceMatch {
  text: string;
  fullId: string;
  prefix: string;
  start: number;
  end: number;
}

// Define a type for the extension methods
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    referenceTransformer: {
      transformReferences: () => ReturnType;
    };
  }
}

export const ReferenceTransformer = Extension.create({
  name: 'referenceTransformer',

  // Store private variables
  addStorage() {
    return {
      editor: null as Editor | null,
      isTransforming: false,
      hasProcessedInitialContent: false,
      // Add a function to the storage that can be called from any method
      scanForReferences: function (this: any) {
        const extension = this;
        if (!extension.editor || extension.isTransforming) {
          return;
        }

        // Get the current state
        const state = extension.editor.state;
        const { doc } = state;

        // More aggressive regex pattern that can catch consecutive references
        // This pattern will match references even when they appear right next to each other
        const pattern =
          /(?:(\w+)\s*:\s*)?{{(inputs|steps)\.([\w-]+)}}(?!\s*<)/g;
        let foundAnyReferences = false;

        // Scan each text node in the document
        doc.descendants((node: ProsemirrorNode, pos: number) => {
          if (!node.isText) return true;

          const text = node.text as string;
          if (!text || !text.includes('{{')) return true;

          // Check for references
          pattern.lastIndex = 0;
          if (pattern.test(text)) {
            foundAnyReferences = true;
          }

          return true;
        });

        // If we found references, force a transformation
        if (foundAnyReferences) {
          // Schedule an additional check if references are still present after the normal transformation
          setTimeout(() => {
            if (!extension.editor || extension.editor.isDestroyed) return;

            const html = extension.editor.getHTML();
            if (html.includes('{{') && html.includes('}}')) {
              // Force another transformation pass
              extension.editor.view.dispatch(
                extension.editor.state.tr.setMeta('transformReferences', true)
              );
            }
          }, 50);
        }
      },
    };
  },

  addInputRules() {
    // Create input rules to transform references as they're typed
    // Make the regex more robust to handle all types of references
    const referenceRegex = /\{\{([^}]+)\}\}(?!\s*<)/g;

    return [
      new InputRule({
        find: referenceRegex,
        handler: ({ state, range, match, chain }) => {
          // Extract the entire content between {{ and }}
          const [_, content] = match;
          // Use the entire content as the reference ID
          const referenceId = content;

          try {
            // Validate the range before modifying
            const docSize = state.doc.content.size;
            if (range.from < 0 || range.to > docSize) {
              return null;
            }

            // Use the chain commands to replace the text with a reference node
            chain()
              .deleteRange({ from: range.from, to: range.to })
              .insertContent({
                type: 'reference',
                attrs: { id: referenceId },
              })
              .run();

            // After inserting a reference, check for adjacent references that might need transformation
            setTimeout(() => {
              if (this.editor && !this.editor.isDestroyed) {
                // Trigger a scan for more references, especially those that might be adjacent
                this.storage.scanForReferences.call(this.storage);
              }
            }, 10);
          } catch (error) {
            console.error('Error in ReferenceTransformer InputRule:', error);
          }

          // Return void to satisfy TypeScript (the chain commands handle the transaction)
          return null;
        },
      }),
    ];
  },

  addProseMirrorPlugins() {
    const extension = this;

    return [
      new Plugin({
        key: ReferenceTransformerKey,

        // Store the editor in storage when plugin is created
        view(editorView) {
          if (extension.editor) {
            extension.storage.editor = extension.editor;
          }

          return {
            update: () => {
              // Update logic if needed
            },
            destroy: () => {
              // Cleanup
            },
          };
        },

        // Add decorations to highlight reference patterns
        props: {
          decorations(state) {
            // Don't add decorations if we're in the middle of a transformation
            if (extension.storage.isTransforming) {
              return DecorationSet.empty;
            }

            const { doc } = state;
            const decorations: Decoration[] = [];

            doc.descendants((node, pos) => {
              if (node.isText) {
                const text = node.text as string;
                let match;

                // Reset the regex
                REFERENCE_DOUBLE_MUSTACHE_PATTERN.lastIndex = 0;

                // Find all matches in this text node
                while (
                  (match = REFERENCE_DOUBLE_MUSTACHE_PATTERN.exec(text)) !==
                  null
                ) {
                  const start = pos + match.index;
                  const end = start + match[0].length;

                  decorations.push(
                    Decoration.inline(start, end, {
                      class: 'reference-highlight',
                      style:
                        'background-color: rgba(0, 123, 255, 0.2); border-radius: 2px;',
                    })
                  );
                }
              }

              return true;
            });

            return DecorationSet.create(doc, decorations);
          },
        },

        // Append transaction to transform references when needed
        appendTransaction(transactions, oldState, newState) {
          // Check if any transaction has changed the document or requested reference transformation
          const docChanged = transactions.some((tr) => tr.docChanged);
          const forceTransform = transactions.some((tr) =>
            tr.getMeta('transformReferences')
          );

          // Skip if we're already transforming to avoid loops
          if (extension.storage.isTransforming) {
            return null;
          }

          // Only process if document changed or transformation is forced
          if (!docChanged && !forceTransform) {
            return null;
          }

          // Get the editor from the extension storage
          const editor = extension.storage.editor;

          if (!editor) {
            return null;
          }

          // Get the reference node type from schema
          const { schema } = newState;
          const referenceType = schema.nodes.reference;

          if (!referenceType) {
            return null;
          }

          // Find text nodes with reference patterns
          const tr = newState.tr;

          // Update the regex pattern to handle all types of references
          // The negative lookahead (?!\s*<) ensures we don't match references already wrapped in HTML tags
          const pattern = /\{\{([^}]+)\}\}(?!\s*<)/g;
          const foundReferences: ReferenceMatch[] = [];

          // Step 1: Collect all text nodes with reference patterns
          newState.doc.descendants((node, pos) => {
            if (!node.isText) return true;

            const text = node.text as string;
            if (!text || !text.includes('{{')) return true;

            let match;
            pattern.lastIndex = 0;

            while ((match = pattern.exec(text)) !== null) {
              const referenceText = match[0];
              const fullId = match[1]; // The entire content between {{ and }}
              const start = pos + match.index;
              const end = start + referenceText.length;

              foundReferences.push({
                text: referenceText,
                fullId,
                prefix: '',
                start,
                end,
              });
            }

            return true;
          });

          // If no references found, don't need to do anything
          if (foundReferences.length === 0) {
            return null;
          }

          // Sort references in reverse order (to maintain correct positions during transformation)
          foundReferences.sort((a, b) => b.start - a.start);

          // Start transformation
          extension.storage.isTransforming = true;

          try {
            // Process each reference
            for (const ref of foundReferences) {
              const { fullId, start, end } = ref;

              // Validate positions
              if (start < 0 || end > newState.doc.content.size) {
                continue;
              }

              // Remember current selection position relative to where we're editing
              const selWasAfterEdit = tr.selection.anchor > end;
              const selectionAnchor = tr.selection.anchor;

              // Create reference node
              const referenceNode = referenceType.create({ id: fullId });

              // Replace the text with the reference node
              tr.replaceWith(start, end, referenceNode);

              // If the selection was after the edit point, we need to adjust it
              // since we've replaced text with a reference node (which may have different size)
              if (selWasAfterEdit) {
                // Calculate the change in size
                const sizeDifference = end - start - 1; // 1 is the size of the reference node
                // Adjust selection if needed
                if (sizeDifference !== 0) {
                  const newSel = tr.mapping.map(
                    selectionAnchor - sizeDifference
                  );
                  tr.setSelection(TextSelection.create(tr.doc, newSel));
                }
              }
            }

            // Mark as transformed
            extension.storage.hasProcessedInitialContent = true;

            // Add meta data to the transaction to signal that a reference transformation occurred
            // This will be used by event listeners to trigger a save
            tr.setMeta('referenceTransformation', {
              count: foundReferences.length,
              timestamp: Date.now(),
              referenceIds: foundReferences.map((ref) => ref.fullId),
            });

            // Return the transaction with the changes
            return tr;
          } catch (error) {
            console.error(
              'ReferenceTransformer ERROR: Failed to transform references',
              error
            );
            return null;
          } finally {
            // Reset the transforming flag
            extension.storage.isTransforming = false;
          }
        },
      }),
    ];
  },

  // Set up the extension when it's created
  onCreate() {
    // Track extension instance in window for debugging
    if (typeof window !== 'undefined') {
      (window as any)._referenceTransformerExtension = this;
    }

    // Store the editor in storage
    if (this.editor) {
      this.storage.editor = this.editor;
    }

    // Store reference to the extension for closure access
    const extension = this;

    // Process initial content after a delay
    setTimeout(() => {
      // Process initial content to transform references
      if (!extension.editor) {
        return;
      }

      // Skip if we've already processed the initial content
      if (extension.storage.hasProcessedInitialContent) {
        return;
      }

      // Check if the editor is empty
      const content = extension.editor.getHTML();
      const containsReferences = content && content.includes('{{');

      if (containsReferences) {
        try {
          // Use a transaction to trigger the transformation
          const tr = extension.editor.state.tr;
          tr.setMeta('transformReferences', true);
          extension.editor.view.dispatch(tr);
        } catch (error) {
          console.error(
            'ReferenceTransformer: Error transforming initial references',
            error
          );
        }
      } else {
        console.warn(
          'ReferenceTransformer DEBUG: No references found in initial content'
        );
      }
    }, 100);
  },

  // Methods for the extension
  onUpdate() {
    // Check if the editor is available
    if (!this.editor || this.storage.isTransforming) {
      return;
    }

    // Get the current content
    const content = this.editor.getHTML();

    // Check if the content contains references that need transformation
    if (content && content.includes('{{') && content.includes('}}')) {
      // Trigger transformation with a short delay to ensure editor state is stable
      setTimeout(() => {
        if (this.editor && !this.editor.isDestroyed) {
          // Perform a more thorough scan for references
          this.storage.scanForReferences.call(this.storage);

          // Then trigger the transformation
          this.editor.view.dispatch(
            this.editor.state.tr.setMeta('transformReferences', true)
          );
        }
      }, 10);
    }
  },

  // Add commands to the extension
  addCommands() {
    return {
      transformReferences:
        () =>
        ({ editor }: { editor: Editor }) => {
          if (!editor) return false;

          try {
            const tr = editor.state.tr;
            tr.setMeta('transformReferences', true);
            editor.view.dispatch(tr);
            return true;
          } catch (error) {
            console.error('Error in transformReferences command:', error);
            return false;
          }
        },
    } as Partial<RawCommands>;
  },
});
